/**
 * Utilitaires pour gérer le stockage local de manière sécurisée
 */

/**
 * Enregistre une valeur dans le localStorage avec gestion des erreurs
 * @param key Clé de stockage
 * @param value Valeur à stocker
 */
export const safeSetItem = (key: string, value: any): void => {
  try {
    // Si la valeur est null ou undefined, on la supprime
    if (value === null || value === undefined) {
      localStorage.removeItem(key);
      return;
    }
    
    // Si c'est une chaîne, on la stocke directement
    if (typeof value === 'string') {
      localStorage.setItem(key, value);
      return;
    }
    
    // Pour les autres types, on les convertit en JSON
    try {
      const jsonValue = JSON.stringify(value);
      localStorage.setItem(key, jsonValue);
    } catch (jsonError) {
      console.error(`Erreur lors de la conversion en JSON pour la clé ${key}:`, jsonError);
      // En cas d'erreur, on stocke une valeur de secours
      localStorage.setItem(key, JSON.stringify({ error: 'Valeur non sérialisable' }));
    }
  } catch (error) {
    console.error(`Erreur lors du stockage de la clé ${key}:`, error);
  }
};

/**
 * Récupère une valeur du localStorage avec gestion des erreurs
 * @param key Clé de stockage
 * @param defaultValue Valeur par défaut si la clé n'existe pas ou en cas d'erreur
 */
export const safeGetItem = <T>(key: string, defaultValue: T): T => {
  try {
    const item = localStorage.getItem(key);
    
    // Si l'élément n'existe pas, retourner la valeur par défaut
    if (item === null) {
      return defaultValue;
    }
    
    // Si c'est la chaîne "[object Object]", c'est une erreur de conversion
    if (item === "[object Object]") {
      console.error(`Valeur invalide détectée pour la clé ${key}: "[object Object]"`);
      localStorage.removeItem(key);
      return defaultValue;
    }
    
    // Cas spécial pour les tokens JWT (commencent par "ey")
    if (key === 'token' && typeof item === 'string' && item.startsWith('ey')) {
      return item as unknown as T;
    }
    
    // Pour les valeurs qui semblent être du JSON, essayer de les parser
    if (item.startsWith('{') || item.startsWith('[')) {
      try {
        return JSON.parse(item) as T;
      } catch (parseError) {
        console.error(`Erreur de parsing JSON pour la clé ${key}:`, parseError);
        // Si ce n'est pas du JSON valide, retourner l'élément tel quel pour les chaînes
        if (typeof defaultValue === 'string') {
          return item as unknown as T;
        }
        return defaultValue;
      }
    }
    
    // Pour les autres types, retourner la valeur telle quelle
    return item as unknown as T;
  } catch (error) {
    console.error(`Erreur lors de la récupération de la clé ${key}:`, error);
    return defaultValue;
  }
};

/**
 * Supprime une clé du localStorage avec gestion des erreurs
 * @param key Clé à supprimer
 */
export const safeRemoveItem = (key: string): void => {
  try {
    localStorage.removeItem(key);
  } catch (error) {
    console.error(`Erreur lors de la suppression de la clé ${key}:`, error);
  }
};

/**
 * Vérifie si une clé existe dans le localStorage
 * @param key Clé à vérifier
 */
export const safeHasItem = (key: string): boolean => {
  try {
    return localStorage.getItem(key) !== null;
  } catch (error) {
    console.error(`Erreur lors de la vérification de la clé ${key}:`, error);
    return false;
  }
};

/**
 * Efface tout le localStorage avec gestion des erreurs
 */
export const safeClear = (): void => {
  try {
    localStorage.clear();
  } catch (error) {
    console.error('Erreur lors de l\'effacement du localStorage:', error);
  }
};
